import BaseRenderer from 'diagram-js/lib/draw/BaseRenderer';
import { getBusinessObject } from 'bpmn-js/lib/util/ModelUtil';
import { append as svgAppend, attr as svgAttr, classes as svgClasses, create as svgCreate, innerSVG } from 'tiny-svg';
import { ReferenceTextFunction } from './ReferenceTextFunction';
import { getBounds, getFillColor, getStrokeColor } from 'bpmn-js/lib/draw/BpmnRenderUtil';
import { isObject } from 'lodash';
import VersionService from '../VersionService';

const HIGH_PRIORITY = 1500;

const modelImageSvg =
    '<svg fill="#000000" width="16" height="16" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMinYMin" class="jam jam-sitemap"><path d=\'M2 14v4h4v-4H2zm12-3H6a1 1 0 0 0-1 1h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h1a3 3 0 0 1 3-3h3V8H8a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1v1h3a3 3 0 0 1 3 3h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h1a1 1 0 0 0-1-1zM8 2v4h4V2H8zm6 12v4h4v-4h-4z\'/></svg>';

export class ReferenceRenderer extends BaseRenderer {
    /**
     * @param {import('diagram-js/lib/core/EventBus').default<null>} eventBus
     * @param {any} bpmnRenderer
     * @param {any} textRenderer
     * @param {any} pathMap
     * @param {any} styles
     * @param {VersionService} versionService
     */
    constructor(eventBus, styles, pathMap, bpmnRenderer, textRenderer, versionService) {
        super(eventBus, HIGH_PRIORITY);

        this.bpmnRenderer = bpmnRenderer;
        this.pathMap = pathMap;
        this.textRenderer = textRenderer;
        this.styles = styles;
        this.versionService = versionService;
    }

    /**
     * @param {{ id: string, labelTarget: any; type: any; }} element
     */
    // @ts-expect-error
    canRender(element) {
        // only render tasks and events (ignore labels)
        return element.type === 'bpmn:Task' && element.id.startsWith('Activities_Reference');
    }

    /**
     * @param {Element} parentGfx
     * @param {number} width
     * @param {number} height
     * @param {any} r
     * @param {number} offset
     * @param {object} attrs
     */
    drawRect(parentGfx, width, height, r, offset, attrs) {
        if (isObject(offset)) {
            attrs = offset;
            offset = 0;
        }

        offset = offset || 0;

        const rect = svgCreate('rect', {
            x: offset,
            y: offset,
            width: width - offset * 2,
            height: height - offset * 2,
            rx: r,
            ry: r,
            ...attrs,
        });

        svgAppend(parentGfx, rect);

        return rect;
    }

    /**
     * @param {any} parentNode
     * @param {any} element
     * @param {any} [attrs]
     * @override
     */
    drawShape(parentNode, element, attrs = {}) {
        const { width, height } = getBounds(element, attrs);
        // @ts-expect-error
        const versionId = this.versionService.getVersionId();

        const shape = this.drawRect(parentNode, width, height, 10, 0, {
            ...attrs,
            fill: getFillColor(element, 'white', attrs.fill),
            fillOpacity: 0.95,
            stroke: getStrokeColor(element, 'black', attrs.stroke),
        });

        let textPrefix = '';
        const { objectId, resourceType } = getBusinessObject(element);
        const fxText = svgCreate('svg');
        svgClasses(fxText).add('djs-label');
        svgAttr(fxText, {
            fill: getStrokeColor(element, 'black', attrs.stroke),
            transform: 'translate(25, 25)',
            viewBox: '-4 -4 24 24',
            width: 24,
            height: 24,
        });
        innerSVG(fxText, modelImageSvg);
        svgAppend(parentNode, fxText);

        ReferenceTextFunction(element.id, objectId, versionId, resourceType, (name) => {
            const labelText = svgCreate('text');
            svgAttr(labelText, {
                fill: getStrokeColor(element, 'black', attrs.stroke),
                transform: `translate(${element.width / 2}, ${element.height / 2})`,
                dominantBaseline: 'middle',
                textAnchor: 'middle',
            });
            svgClasses(labelText).add('djs-label');
            // @ts-expect-error
            svgAppend(labelText, document.createTextNode(textPrefix + name));
            svgAppend(parentNode, labelText);
        });

        return shape;
    }
}

ReferenceRenderer.$inject = ['eventBus', 'styles', 'pathMap', 'bpmnRenderer', 'textRenderer', 'versionService'];
